/**
 * PR Monitoring Page for SWRM-30
 * 
 * Main page for comprehensive PR monitoring and management including:
 * - Dashboard overview with key metrics
 * - Active monitoring sessions
 * - Blocking queue management
 * - Analytics and reporting
 * - Real-time updates via WebSocket
 */

import React, { useState } from 'react';
import { PRStatusDashboard, PRBlockingQueue } from '../components/PRMonitoring';
import { usePRMonitoringDashboard } from '../hooks/usePRMonitoring';

const PRMonitoring: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { dashboard } = usePRMonitoringDashboard();

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      count: dashboard?.active_monitoring_sessions || 0
    },
    {
      id: 'blocking-queue',
      label: 'Blocking Queue',
      count: dashboard?.blocked_workflows || 0
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">PR Monitoring</h1>
          <p className="mt-2 text-gray-600">
            Monitor and manage pull request status across all workflows
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <PRStatusDashboard className="w-full" />
          )}
          
          {activeTab === 'blocking-queue' && (
            <PRBlockingQueue className="w-full" />
          )}
        </div>
      </div>
    </div>
  );
};

export default PRMonitoring;
